import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useState } from "react"
import { maskEmail } from "@/features/workspaces/workspace-model"
import { initials } from "@/lib/initials"
import { sessionQuery } from "@/lib/queries"
import { useSignOut } from "@/lib/session"
import { Avatar } from "./ui/avatar"
import { MenuSeparator, menuItemClass, Popover } from "./ui/popover"

/**
 * The avatar menu, on every page (wave B2, decision 0057 §4.3; UX pass phase 2, P11): name and masked email, then Your
 * page (in Knowledge: your name, GitHub, who you are and how you work) · Account (sign-in & security, your plans,
 * connected apps) · Sign out. Workspace settings live in the header and the workspace picker.
 */
export function AvatarMenu({ workspaceId }: { workspaceId: string }) {
  const [open, setOpen] = useState(false)
  const session = useQuery(sessionQuery).data
  const close = () => setOpen(false)
  const name = session?.user.name || session?.user.email || "You"
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      title="Account"
      align="end"
      className="w-64"
      trigger={(props) => (
        <button type="button" {...props} aria-label="Account menu" className="tap rounded-full">
          <Avatar label={initials(name)} shape="circle" />
        </button>
      )}
    >
      <AvatarMenuItems workspaceId={workspaceId} onChoose={close} />
    </Popover>
  )
}

/** The menu's rows (rendered by the popover when open; exported for tests). */
export function AvatarMenuItems({ workspaceId, onChoose }: { workspaceId: string; onChoose: () => void }) {
  const session = useQuery(sessionQuery).data
  const signOut = useSignOut()
  const name = session?.user.name || session?.user.email || "You"
  return (
    <>
      <div className="px-2.5 pt-1.5 pb-2">
        <div className="truncate font-semibold">{name}</div>
        <div className="truncate font-mono text-[12px] text-muted">{maskEmail(session?.user.email)}</div>
      </div>
      <MenuSeparator />
      {session?.user.id && (
        // decision 0086: the person's own page in Knowledge (who they are, how they work, their defaults)
        <Link
          role="menuitem"
          to="/w/$workspaceId/knowledge/people/$userId"
          params={{ workspaceId, userId: session.user.id }}
          onClick={onChoose}
          className={menuItemClass}
        >
          Your page
        </Link>
      )}
      {/* UX pass phase 2 (P11): Your page (name, GitHub, how you work) · Account (sign-in, plans, apps) · Sign out */}
      <Link
        role="menuitem"
        to="/w/$workspaceId/account"
        params={{ workspaceId }}
        onClick={onChoose}
        className={menuItemClass}
      >
        Account
      </Link>
      <MenuSeparator />
      <button
        type="button"
        role="menuitem"
        className={menuItemClass}
        onClick={() => {
          onChoose()
          void signOut()
        }}
      >
        Sign out
      </button>
    </>
  )
}
